/**
 * FeedbackProvider - Provides beta feedback components throughout the app
 * Can be easily removed when exiting beta by removing this provider
 */

import { useState, createContext, useContext, ReactNode } from 'react';
import { FeedbackWidget, FeatureSuggestionModal } from '@/components/feedback';

interface FeedbackContextType {
  openFeedback: () => void;
  openFeatureSuggestion: () => void;
}

const FeedbackContext = createContext<FeedbackContextType | null>(null);

export function useFeedbackContext() {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedbackContext must be used within FeedbackProvider');
  }
  return context;
}

interface FeedbackProviderProps {
  children: ReactNode;
  /** Set to false to disable feedback system (e.g., when exiting beta) */
  enabled?: boolean;
}

export function FeedbackProvider({ children, enabled = true }: FeedbackProviderProps) {
  const [isFeatureSuggestionOpen, setIsFeatureSuggestionOpen] = useState(false);

  const contextValue: FeedbackContextType = {
    openFeedback: () => {
      // The FeedbackWidget handles its own open state
      // This is for programmatic access if needed
    },
    openFeatureSuggestion: () => setIsFeatureSuggestionOpen(true),
  };

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <FeedbackContext.Provider value={contextValue}>
      {children}

      {/* Feedback Widget - always visible on protected routes */}
      <FeedbackWidget onSuggestFeature={() => setIsFeatureSuggestionOpen(true)} />

      {/* Feature Suggestion Modal */}
      <FeatureSuggestionModal
        isOpen={isFeatureSuggestionOpen}
        onClose={() => setIsFeatureSuggestionOpen(false)}
      />
    </FeedbackContext.Provider>
  );
}
